import { useMemo, useState } from "react";
import {
  Sparkles,
  Globe,
  Terminal,
  FileText,
  Database,
  Search,
  Mail,
  Code,
  FolderOpen,
  Pencil,
  Eye,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  RefreshCw,
  type LucideIcon,
} from "lucide-react";

export interface ActivityEvent {
  toolUseId: string;
  name: string;
  status: "started" | "completed" | "failed";
  /** Tool input args (JSON string). */
  args?: string;
  durationMs?: number;
  /** Failure reason for failed steps. */
  error?: string;
  /** Tool output/result for completed steps. */
  resultPreview?: string;
  timestamp: number;
}

const TOOL_ICONS: Record<string, LucideIcon> = {
  web_search: Globe,
  web_fetch: Globe,
  browser: Globe,
  exec: Terminal,
  bash: Terminal,
  read: FileText,
  read_file: FileText,
  write: Pencil,
  write_file: Pencil,
  edit_file: Pencil,
  list_directory: FolderOpen,
  glob: FolderOpen,
  grep: Search,
  search: Search,
  memory_search: Database,
  memory: Database,
  database: Database,
  sql: Database,
  email: Mail,
  send_email: Mail,
  code: Code,
  image: Eye,
  screenshot: Eye,
};

export function getToolIcon(toolName: string): LucideIcon {
  const lower = toolName.toLowerCase().replace(/[^a-z_]/g, "");
  for (const [key, icon] of Object.entries(TOOL_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return Terminal;
}

export function formatToolName(name: string): string {
  return name
    .replace(/^mcp__[^_]+__/, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase();
}

export function formatDuration(ms?: number): string {
  if (!ms || ms < 0) return "";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

/** Pretty-print a JSON string; fall back to the raw text if it isn't JSON. */
function prettyJson(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return raw;
  }
}

function StatusIndicator({ status }: { status: ActivityEvent["status"] }) {
  if (status === "started") {
    return <Loader2 size={11} className="shrink-0 animate-spin text-accent" />;
  }
  if (status === "completed") {
    return <CheckCircle2 size={11} className="shrink-0 text-green-600" />;
  }
  return <XCircle size={11} className="shrink-0 text-red-500" />;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        });
      }}
      className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] text-text-muted hover:bg-surface-hover hover:text-text-secondary"
      title="Copy"
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  );
}

/** A labelled args/output/error block: pretty JSON, expand-to-full, copy. */
export function DetailField({ label, value, tone }: { label: string; value: string; tone?: "error" }) {
  const pretty = useMemo(() => prettyJson(value), [value]);
  const [showAll, setShowAll] = useState(false);
  const long = pretty.length > 600;
  const shown = showAll || !long ? pretty : `${pretty.slice(0, 600)}…`;
  return (
    <div className="mt-1.5">
      <div className="mb-0.5 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">{label}</span>
        <div className="flex items-center gap-0.5">
          {long && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowAll((v) => !v);
              }}
              className="rounded px-1 py-0.5 text-[10px] text-text-muted hover:bg-surface-hover hover:text-text-secondary"
            >
              {showAll ? "show less" : "show full"}
            </button>
          )}
          <CopyButton text={pretty} />
        </div>
      </div>
      <pre
        className={`max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md border px-2 py-1.5 text-[11px] leading-relaxed ${
          tone === "error"
            ? "border-red-500/30 bg-red-500/5 text-red-700 dark:text-red-300"
            : "border-border/60 bg-background/70 text-text-secondary"
        }`}
      >
        {shown}
      </pre>
    </div>
  );
}

interface StepGroup {
  key: string;
  name: string;
  attempts: ActivityEvent[];
}

/** Normalise args so semantically-identical calls group together. */
function normArgs(a?: string): string {
  if (!a) return "";
  try {
    return JSON.stringify(JSON.parse(a));
  } catch {
    return a.trim();
  }
}

/** Group consecutive identical (same tool + same args) calls as retry attempts. */
function groupSteps(events: ActivityEvent[]): StepGroup[] {
  const groups: StepGroup[] = [];
  for (const e of events) {
    const key = `${e.name}|${normArgs(e.args)}`;
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.attempts.push(e);
    else groups.push({ key, name: e.name, attempts: [e] });
  }
  return groups;
}

/** One attempt's detail panel — args + output/error. */
function AttemptDetail({ event, attemptLabel }: { event: ActivityEvent; attemptLabel?: string }) {
  return (
    <div className="rounded-md bg-surface-hover/40 px-2 py-1.5">
      {attemptLabel && (
        <div className="mb-0.5 flex items-center gap-1.5 text-[10px] font-medium text-text-muted">
          <StatusIndicator status={event.status} />
          <span>{attemptLabel}</span>
          {event.durationMs != null && event.status !== "started" && <span>· {formatDuration(event.durationMs)}</span>}
        </div>
      )}
      {event.args && <DetailField label="Input" value={event.args} />}
      {event.resultPreview && <DetailField label="Output" value={event.resultPreview} />}
      {event.error && <DetailField label="Error" value={event.error} tone="error" />}
      {!event.args && !event.resultPreview && !event.error && (
        <p className="m-0 text-[11px] italic text-text-muted">No input or output captured for this step.</p>
      )}
    </div>
  );
}

/** A group row: header (tool, outcome, attempt count, duration) + expandable detail. */
function StepGroupRow({ group, defaultOpen }: { group: StepGroup; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const Icon = getToolIcon(group.name);
  const final = group.attempts[group.attempts.length - 1];
  const total = group.attempts.length;
  const retried = total > 1;
  return (
    <div className="rounded-md">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 px-1 py-1 text-left text-[11px] leading-relaxed hover:bg-surface-hover/60"
      >
        {open ? (
          <ChevronDown size={11} className="mt-0.5 shrink-0 text-text-muted" />
        ) : (
          <ChevronRight size={11} className="mt-0.5 shrink-0 text-text-muted" />
        )}
        <StatusIndicator status={final.status} />
        <Icon size={11} className="mt-0.5 shrink-0 text-text-muted" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className={`font-mono ${final.status === "failed" ? "text-red-500" : "text-text-secondary"}`}>
              {formatToolName(group.name)}
            </span>
            {retried && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                <RefreshCw size={9} /> attempt {total} of {total}
              </span>
            )}
            {final.durationMs != null && final.status !== "started" && (
              <span className="text-[10px] text-text-muted">{formatDuration(final.durationMs)}</span>
            )}
          </div>
          {!open && (final.error || final.resultPreview || final.args) && (
            <div
              className={`truncate ${final.status === "failed" ? "text-red-500" : "text-text-muted"}`}
              title={final.error || final.resultPreview || final.args}
            >
              {final.error || final.resultPreview || final.args}
            </div>
          )}
        </div>
      </button>
      {open && (
        <div className="ml-5 space-y-1.5 pb-1.5">
          {retried
            ? group.attempts.map((a, i) => (
                <AttemptDetail key={a.toolUseId} event={a} attemptLabel={`attempt ${i + 1} of ${total}`} />
              ))
            : <AttemptDetail event={final} />}
        </div>
      )}
    </div>
  );
}

/**
 * Renders an agent turn's tool activity — both live (streaming) and persisted
 * (a completed message's saved trace).
 *
 * The header summarises real outcomes (done / failed / running) and flags
 * retries, so a collapsed glance reflects whether the agent struggled. Expand
 * to see each step grouped by retry, with full input args, the returned output,
 * and the error reason for failures.
 *
 * Live: shows the most-recent step inline so there's progress feedback without
 * the box growing unbounded. Persisted: collapsed to the header until opened.
 */
export function ActivityTimeline({ events, persisted = false }: { events: ActivityEvent[]; persisted?: boolean }) {
  const [expanded, setExpanded] = useState(false);

  const groups = useMemo(() => groupSteps(events), [events]);
  const done = events.filter((e) => e.status === "completed").length;
  const failed = events.filter((e) => e.status === "failed").length;
  const running = events.filter((e) => e.status === "started").length;
  const retried = groups.some((g) => g.attempts.length > 1);

  if (events.length === 0) return null;

  // Live compact view: the freshest step, so the user sees what's happening now.
  const latest = events[events.length - 1];

  return (
    <div className="mb-2 rounded-lg border border-border/60 bg-surface-hover/40">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-[11px] text-text-secondary hover:bg-surface-hover"
      >
        <Sparkles size={11} className="text-accent" />
        <span className="font-medium">
          {events.length} tool step{events.length === 1 ? "" : "s"}
        </span>
        {running > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] text-text-muted">
            <Loader2 size={10} className="animate-spin" />
            {running} running
          </span>
        )}
        {failed > 0 && (
          <span className="text-[10px] font-medium text-red-500">{failed} failed</span>
        )}
        {done > 0 && <span className="text-[10px] text-text-muted">{done} done</span>}
        {retried && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
            <RefreshCw size={9} /> retried
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-text-muted">
          {expanded ? (
            <>
              <ChevronDown size={12} /> collapse
            </>
          ) : (
            <>
              <ChevronRight size={12} /> details
            </>
          )}
        </span>
      </button>

      {expanded ? (
        <div className="space-y-0.5 border-t border-border/60 px-1.5 py-1.5">
          {groups.map((g) => (
            <StepGroupRow key={g.key} group={g} />
          ))}
        </div>
      ) : (
        !persisted &&
        running > 0 && (
          // Live progress hint — the current step, one line.
          <div className="flex items-center gap-2 border-t border-border/60 px-2.5 py-1.5 text-[11px] text-text-muted">
            <StatusIndicator status={latest.status} />
            <span className="font-mono text-text-secondary">{formatToolName(latest.name)}</span>
            {latest.args && <span className="min-w-0 flex-1 truncate" title={latest.args}>{latest.args}</span>}
          </div>
        )
      )}
    </div>
  );
}
